import React, { useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, Loader2, Check, Upload, Trash2, Link as LinkIcon } from 'lucide-react';
import { Button } from './ui/button';
import { adminTyreFitmentsAPI, uploadsAPI } from '../services/api';

const BrandImageRow = ({ brand, img, busy, onUpload, onApplyUrl, onDelete }) => {
  const [url, setUrl] = useState('');
  const [urlMode, setUrlMode] = useState(false);
  const fileRef = useRef(null);
  const isBusy = busy === brand;
  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-100 p-2" data-testid={`brand-image-row-${brand}`}>
      <div className="w-16 h-16 rounded bg-gray-50 overflow-hidden flex items-center justify-center shrink-0 border border-gray-100">
        {img ? <img src={uploadsAPI.resolveUrl(img)} alt={brand} className="w-full h-full object-cover" data-testid={`brand-image-preview-${brand}`} /> : <ImageIcon size={18} className="text-gray-300" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">{brand} {img ? <Check size={12} className="inline text-green-600" /> : <span className="text-[11px] font-normal text-amber-600">sans photo</span>}</p>
        {urlMode ? (
          <div className="flex gap-1 mt-1">
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://… URL de la photo" autoFocus className="flex-1 min-w-0 border border-gray-300 rounded px-2 py-1 text-xs" data-testid={`brand-image-url-${brand}`} />
            <Button size="sm" variant="outline" disabled={isBusy || !url.trim()} onClick={async () => { await onApplyUrl(brand, url.trim()); setUrl(''); setUrlMode(false); }} data-testid={`brand-image-apply-${brand}`}>{isBusy ? <Loader2 size={12} className="animate-spin" /> : 'Appliquer'}</Button>
            <Button size="sm" variant="ghost" onClick={() => setUrlMode(false)} data-testid={`brand-image-url-cancel-${brand}`}>Annuler</Button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-1 mt-1">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid={`brand-image-file-${brand}`} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onUpload(brand, f); }} />
            <Button size="sm" variant="outline" disabled={isBusy} onClick={() => fileRef.current?.click()} data-testid={`brand-image-upload-${brand}`} title="Téléverser une photo depuis votre ordinateur">
              {isBusy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} className="mr-1" />}{img ? 'Remplacer' : 'Ajouter'}
            </Button>
            <Button size="sm" variant="ghost" disabled={isBusy} onClick={() => setUrlMode(true)} data-testid={`brand-image-by-url-${brand}`} title="Utiliser une URL"><LinkIcon size={12} className="mr-1" /> URL</Button>
            {img && (
              <Button size="sm" variant="ghost" disabled={isBusy} onClick={() => onDelete(brand)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`brand-image-delete-${brand}`} title="Supprimer la photo (retirée aussi des pneus de la marque)"><Trash2 size={12} className="mr-1" /> Supprimer</Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const TyreBrandImagesCard = ({ onApplied }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [open, setOpen] = useState(false);

  const load = () => adminTyreFitmentsAPI.brandImages().then(setData).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return null;

  const run = async (brand, fn, okMsg) => {
    setBusy(brand);
    try { await fn(); await load(); onApplied?.(okMsg); } catch (e) { onApplied?.(e.response?.data?.detail || 'Erreur lors de la mise à jour de la photo', 'error'); } finally { setBusy(''); }
  };
  const applyUrl = (brand, url) => run(brand, () => adminTyreFitmentsAPI.setBrandImages({ [brand]: url }), `Photo ${brand} appliquée à tous ses pneus`);
  const upload = (brand, file) => run(brand, async () => { const up = await uploadsAPI.uploadImage(file); await adminTyreFitmentsAPI.setBrandImages({ [brand]: up.url }); }, `Photo ${brand} téléversée et appliquée à tous ses pneus`);
  const remove = (brand) => {
    if (!window.confirm(`Supprimer la photo de la marque ${brand} ? Elle sera aussi retirée des pneus de cette marque.`)) return;
    return run(brand, () => adminTyreFitmentsAPI.deleteBrandImage(brand), `Photo ${brand} supprimée`);
  };
  const brands = [...data.items.map((i) => i.brand), ...data.missing];

  return (
    <div className="bg-white rounded-lg border border-gray-200 mb-6" data-testid="tyre-brand-images">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3 text-left" data-testid="tyre-brand-images-toggle">
        <span className="font-semibold text-gray-900 flex items-center gap-2"><ImageIcon size={16} className="text-orange-500" /> Photos produit par marque de pneu <span className="text-xs font-normal text-gray-500">— {data.items.length} marque(s) avec photo{data.missing.length ? `, ${data.missing.length} sans` : ''}</span></span>
        <span className="text-xs text-gray-500">{open ? 'Réduire' : 'Gérer'}</span>
      </button>
      {open && (
        <div className="px-4 pb-4">
          <p className="text-xs text-gray-500 mb-3">La photo d'une marque est appliquée automatiquement à tous les pneus de cette marque. Ajoutez / remplacez depuis votre ordinateur ou par URL, ou supprimez-la.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {brands.map((b) => <BrandImageRow key={b} brand={b} img={data.items.find((i) => i.brand === b)?.image} busy={busy} onUpload={upload} onApplyUrl={applyUrl} onDelete={remove} />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default TyreBrandImagesCard;
